import React from "react";
import { cn } from "../../utils/tailwindUtils";

/**
 * Minimal, SAFE markdown renderer for copilot answers.
 *
 * The text comes from an LLM, so it is untrusted input. This builds real React
 * elements instead of using dangerouslySetInnerHTML, which means a model that
 * echoes `<img onerror=...>` renders as literal text rather than executing.
 *
 * Supported: **bold**, *italic*, `code`, headings, bullet/numbered lists,
 * pipe tables and blank-line spacing — the subset the backend prompt can emit.
 */

const INLINE_PATTERN = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;

/** Split one line into bold / italic / code / link / plain React nodes. */
const renderInline = (text, keyPrefix) => {
    if (!text) return null;

    return text
        .split(INLINE_PATTERN)
        .filter((part) => part !== "" && part !== undefined)
        .map((part, index) => {
            const key = `${keyPrefix}-${index}`;

            if (part.startsWith("[") && part.includes("](") && part.endsWith(")")) {
                const match = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
                if (match) {
                    const label = match[1];
                    const url = match[2];
                    return (
                        <a
                            key={key}
                            href={url}
                            onClick={(e) => {
                                if (url.startsWith("/")) {
                                    e.preventDefault();
                                    window.history.pushState({}, "", url);
                                    window.dispatchEvent(new Event("popstate"));
                                }
                            }}
                            className="inline-flex items-center gap-1 font-semibold text-sidebar-primary underline decoration-sidebar-primary/40 underline-offset-2 hover:decoration-sidebar-primary cursor-pointer"
                        >
                            {label}
                        </a>
                    );
                }
            }

            if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
                return (
                    <strong key={key} className="font-semibold">
                        {part.slice(2, -2)}
                    </strong>
                );
            }
            if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
                return (
                    <code
                        key={key}
                        className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-sidebar-primary dark:text-teal-300"
                    >
                        {part.slice(1, -1)}
                    </code>
                );
            }
            if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
                return (
                    <em key={key} className="italic">
                        {part.slice(1, -1)}
                    </em>
                );
            }
            return <React.Fragment key={key}>{part}</React.Fragment>;
        });
};

const isTableRow = (line) => {
    const trimmed = line.trim();
    return trimmed.startsWith("|") && trimmed.endsWith("|") && trimmed.length > 2;
};

const splitTableCells = (line) =>
    line
        .trim()
        .slice(1, -1)
        .split("|")
        .map((cell) => cell.trim());

/** A markdown pipe table. Scrolls inside its own container on narrow screens. */
const MarkdownTable = ({ rows }) => {
    if (!rows.length) return null;
    const [head, ...body] = rows;

    return (
        <div className="my-2 overflow-x-auto rounded-lg border border-border">
            <table className="w-full border-collapse text-xs">
                <thead>
                    <tr className="bg-muted/60">
                        {head.map((cell, index) => (
                            <th
                                key={index}
                                className="whitespace-nowrap px-3 py-2 text-left font-semibold text-foreground"
                            >
                                {renderInline(cell, `th-${index}`)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {body.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-t border-border">
                            {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="px-3 py-2 text-muted-foreground">
                                    {renderInline(cell, `td-${rowIndex}-${cellIndex}`)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const CopilotMarkdown = ({ content, className }) => {
    if (!content) return null;

    const blocks = [];
    let tableRows = [];
    let listItems = null;

    const flushTable = (key) => {
        if (tableRows.length) {
            blocks.push(<MarkdownTable key={`table-${key}`} rows={tableRows} />);
            tableRows = [];
        }
    };

    const flushList = (key) => {
        if (listItems && listItems.items.length) {
            const ListTag = listItems.ordered ? "ol" : "ul";
            blocks.push(
                <ListTag
                    key={`list-${key}`}
                    className={cn(
                        "my-1.5 space-y-1 pl-5",
                        listItems.ordered ? "list-decimal" : "list-disc"
                    )}
                >
                    {listItems.items.map((item, index) => (
                        <li key={index} className="leading-relaxed marker:text-sidebar-primary">
                            {renderInline(item, `li-${key}-${index}`)}
                        </li>
                    ))}
                </ListTag>
            );
        }
        listItems = null;
    };

    String(content)
        .split("\n")
        .forEach((line, index) => {
            // Tables buffer until a non-table line appears.
            if (isTableRow(line)) {
                flushList(index);
                // Skip the |---|---| separator row.
                if (!/^[\s|:-]+$/.test(line.trim())) tableRows.push(splitTableCells(line));
                return;
            }
            flushTable(index);

            const trimmed = line.trim();

            // Bullet list
            const bulletMatch = trimmed.match(/^[-*•]\s+(.*)$/);
            if (bulletMatch) {
                if (!listItems || listItems.ordered) {
                    flushList(index);
                    listItems = { ordered: false, items: [] };
                }
                listItems.items.push(bulletMatch[1]);
                return;
            }

            // Numbered list
            const orderedMatch = trimmed.match(/^\d+[.)]\s+(.*)$/);
            if (orderedMatch) {
                if (!listItems || !listItems.ordered) {
                    flushList(index);
                    listItems = { ordered: true, items: [] };
                }
                listItems.items.push(orderedMatch[1]);
                return;
            }

            flushList(index);

            if (!trimmed) {
                blocks.push(<div key={`gap-${index}`} className="h-1.5" />);
                return;
            }

            const headingMatch = trimmed.match(/^(#{1,4})\s+(.*)$/);
            if (headingMatch) {
                const level = headingMatch[1].length;
                blocks.push(
                    <p
                        key={`h-${index}`}
                        className={cn(
                            "mb-1 mt-2 font-semibold text-foreground",
                            level <= 2 ? "text-sm" : "text-[13px]"
                        )}
                    >
                        {renderInline(headingMatch[2], `h-${index}`)}
                    </p>
                );
                return;
            }

            blocks.push(
                <p key={`p-${index}`} className="leading-relaxed">
                    {renderInline(line, `p-${index}`)}
                </p>
            );
        });

    flushList("end");
    flushTable("end");

    return <div className={cn("space-y-0.5 break-words", className)}>{blocks}</div>;
};

export default CopilotMarkdown;
